import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { PluginSettingDef } from '../types'
import { ToggleSwitch } from '@tendbot/ui'
import { usePlugins } from '../usePlugins'

interface PluginSettingsProps {
  pluginId: string
  settingDefs?: PluginSettingDef[]
  userSettings?: Record<string, unknown>
}

export default function PluginSettings({ pluginId, settingDefs, userSettings }: PluginSettingsProps): React.JSX.Element | null {
  const { t } = useTranslation('plugins')
  const { updateSettings } = usePlugins()
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleChange = useCallback(async (settingId: string, value: unknown) => {
    setSaving(settingId)
    setError(null)
    try {
      await updateSettings(pluginId, { [settingId]: value })
    } catch (err) {
      setError(t('card.saveFailed', { setting: settingId }))
      console.error('Settings update failed:', err)
    } finally {
      setSaving(null)
    }
  }, [pluginId, updateSettings, t])

  if (!settingDefs?.length) {
    return (
      <div className="mt-3 py-2">
        <p className="text-xs text-pearl/60">{t('card.noSettings')}</p>
      </div>
    )
  }

  const getValue = (def: PluginSettingDef) => {
    if (userSettings && def.id in userSettings) return userSettings[def.id]
    return def.default
  }

  return (
    <div className="mt-3 space-y-3">
      {error && (
        <p className="text-xs text-rose">{error}</p>
      )}
      {settingDefs.map((setting) => {
        const currentValue = getValue(setting)
        const isSaving = saving === setting.id

        return (
          <div key={setting.id} className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <span className="text-sm text-pearl">{setting.label}</span>
              {setting.description && (
                <p className="text-xs text-pearl/50">{setting.description}</p>
              )}
            </div>
            <div className="shrink-0">
              {setting.type === 'toggle' && (
                <ToggleSwitch
                  checked={!!currentValue}
                  onChange={() => handleChange(setting.id, !currentValue)}
                  disabled={isSaving}
                />
              )}
              {setting.type === 'number' && (
                <input
                  type="number"
                  value={typeof currentValue === 'number' ? currentValue : Number(setting.default)}
                  onChange={(e) => handleChange(setting.id, Number(e.target.value))}
                  disabled={isSaving}
                  className="w-16 bg-charcoal border border-white/10 rounded-md px-2 py-1 text-sm text-gold text-right focus:border-gold/40 focus:outline-none disabled:opacity-50"
                />
              )}
              {setting.type === 'select' && (
                <select
                  value={String(currentValue)}
                  onChange={(e) => handleChange(setting.id, e.target.value)}
                  disabled={isSaving}
                  className="bg-charcoal border border-white/10 rounded-md px-2 py-1 text-sm text-gold focus:border-gold/40 focus:outline-none disabled:opacity-50"
                >
                  {(setting.options ?? []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
              {setting.type === 'multi-select' && (
                <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                  {(setting.options ?? []).map(opt => {
                    const selected = Array.isArray(currentValue) && currentValue.includes(opt)
                    return (
                      <button
                        key={opt}
                        onClick={() => {
                          const current = Array.isArray(currentValue) ? currentValue : []
                          const next = selected
                            ? current.filter((v: string) => v !== opt)
                            : [...current, opt]
                          handleChange(setting.id, next)
                        }}
                        disabled={isSaving}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors disabled:opacity-50 ${
                          selected
                            ? 'bg-gold/20 text-gold border border-gold/30'
                            : 'bg-charcoal text-pearl/50 border border-white/10 hover:border-white/20'
                        }`}
                      >
                        {opt}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
